import * as React from 'react';
import type { Node as TiptapNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import { offset } from '@floating-ui/react';

// Hooks
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';
import { useIsMobile } from '../../../../app/rte/hooks/use-mobile';
import { useUiEditorState } from '../../../../app/rte/hooks/use-ui-editor-state';
import { selectNodeAndHideFloating } from '../../../../app/rte/hooks/use-floating-toolbar-visibility';

// UI Components
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@cotera/client/v0/components/ui/dropdown-menu';

// Tiptap UI
import { useImageDownload } from '../image-download-button';
import { DuplicateShortcutBadge, useDuplicate } from '../duplicate-button';
import {
  CopyToClipboardShortcutBadge,
  useCopyToClipboard,
} from '../copy-to-clipboard-button';
import { DeleteNodeShortcutBadge, useDeleteNode } from '../delete-node-button';
import {
  CopyAnchorLinkShortcutBadge,
  useCopyAnchorLink,
} from '../copy-anchor-link-button';
import { useResetAllFormatting } from '../reset-all-formatting-button';
import { SlashCommandTriggerButton } from '../slash-command-trigger-button';
import { TEXT_COLORS, useColorText } from '../color-text-button';
import { HIGHLIGHT_COLORS, useColorHighlight } from '../color-highlight-button';
// Removed unused imports - now using turn-into configurations
import type { RecentColor } from '../color-text-popover';
import { getColorByValue, useRecentColors } from '../color-text-popover';

import { DragHandle } from '@tiptap/extension-drag-handle-react';

// Utils
import { getNodeDisplayName, isTextSelectionValid } from '../../utils';
import { getAvailableTurnIntoConfigurations } from '../../../../app/rte/lib/turn-into-utils';

import type {
  ColorMenuItemProps,
  DragContextMenuProps,
  MenuItemProps,
  NodeChangeData,
} from './drag-context-menu-types';
import { useMenuActionVisibility } from './drag-context-menu-hooks';

// Icons
import { Icon } from '../../../icon/icon';
import { TextColorSmallIcon } from '../../../icon/text-color-small-icon';
import { Tooltip } from '@cotera/client/app/components';
import { flushSync } from 'react-dom';

const useNodeTransformActions = (
  providedEditor?: ReturnType<typeof useTiptapEditor>['editor']
) => {
  const { editor } = useTiptapEditor(providedEditor);
  const turnIntoConfigs = getAvailableTurnIntoConfigurations(editor);

  return turnIntoConfigs.map((config) => ({
    icon: config.icon,
    label: config.label,
    onClick: () => editor && config.execute(editor),
    disabled: !editor || !config.canTurnInto(editor),
    isActive: false, // Turn-into actions don't have active states
    available: true, // Already filtered by getAvailableTurnIntoConfigurations
  }));
};

const BaseMenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  onClick,
  disabled = false,
  isActive = false,
  shortcutBadge,
}) => {
  const IconComponent =
    typeof icon === 'string' ? (
      <Icon icon={icon} className="tiptap-button-icon" />
    ) : (
      React.createElement(icon, { className: 'tiptap-button-icon' })
    );

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      onClick={onClick}
      disabled={disabled}
      className="flex justify-between"
    >
      <div className="flex items-center gap-2">
        {IconComponent}
        <span className="tiptap-button-text">{label}</span>
      </div>
      {shortcutBadge}
    </DropdownMenuItem>
  );
};

const SubMenuTrigger: React.FC<{
  icon: React.ComponentType<{ className?: string }> | (() => JSX.Element);
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {React.createElement(icon, {
          className: 'tiptap-button-icon mr-2 text-muted-foreground',
        })}
        <span className="tiptap-button-text">{label}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
        {children}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

const TextColorMenuItem: React.FC<ColorMenuItemProps> = ({ color }) => {
  const { addRecentColor } = useRecentColors();
  const { isActive, handleColorText, label } = useColorText({
    label: color.label,
    textColor: color.value,
    onApplied: ({ color, label }) =>
      addRecentColor({ type: 'text', label, value: color }),
  });

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      onClick={handleColorText}
    >
      <span className="tiptap-button-color-text" style={{ color: color.value }}>
        <TextColorSmallIcon
          className="tiptap-button-icon"
          style={{ color: color.value, flexGrow: 1 }}
        />
      </span>
      <span className="tiptap-button-text">{label}</span>
    </DropdownMenuItem>
  );
};

const HighlightColorMenuItem: React.FC<ColorMenuItemProps> = ({ color }) => {
  const { addRecentColor } = useRecentColors();
  const { isActive, handleColorHighlight, label } = useColorHighlight({
    label: color.label,
    highlightColor: color.value,
    onApplied: ({ color, label }) =>
      addRecentColor({ type: 'highlight', label, value: color }),
  });

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      onClick={handleColorHighlight}
    >
      <span
        className="tiptap-button-highlight"
        style={{ '--highlight-color': color.value } as React.CSSProperties}
      />
      <span className="tiptap-button-text">{label}</span>
    </DropdownMenuItem>
  );
};

const RecentColorMenuItem: React.FC<{
  colorObj: RecentColor;
}> = ({ colorObj }) => {
  const colorSet = colorObj.type === 'text' ? TEXT_COLORS : HIGHLIGHT_COLORS;
  const color = getColorByValue(colorObj.value, colorSet);

  const ColorComponent =
    colorObj.type === 'text' ? TextColorMenuItem : HighlightColorMenuItem;

  return <ColorComponent color={color} />;
};

const ColorActionGroup: React.FC = () => {
  const { recentColors, isInitialized } = useRecentColors();

  return (
    <SubMenuTrigger
      icon={(props) => <Icon icon="paint-bucket" {...props} />}
      label="Color"
    >
      {/* Recent Colors */}
      {isInitialized && recentColors.length > 0 && (
        <DropdownMenuGroup>
          <DropdownMenuLabel>Recent colors</DropdownMenuLabel>
          {recentColors.map((colorObj) => (
            <RecentColorMenuItem key={colorObj.value} colorObj={colorObj} />
          ))}
          <DropdownMenuSeparator />
        </DropdownMenuGroup>
      )}

      {/* Text Colors */}
      <DropdownMenuGroup>
        <DropdownMenuLabel>Text color</DropdownMenuLabel>
        {TEXT_COLORS.map((textColor) => (
          <TextColorMenuItem key={textColor.value} color={textColor} />
        ))}
      </DropdownMenuGroup>

      <DropdownMenuSeparator />

      {/* Highlight Colors */}
      <DropdownMenuGroup>
        <DropdownMenuLabel>Highlight color</DropdownMenuLabel>
        {HIGHLIGHT_COLORS.map((highlightColor) => (
          <HighlightColorMenuItem
            key={highlightColor.value}
            color={highlightColor}
          />
        ))}
      </DropdownMenuGroup>
    </SubMenuTrigger>
  );
};

const TransformActionGroup: React.FC<{
  editor?: ReturnType<typeof useTiptapEditor>['editor'];
}> = ({ editor }) => {
  const actions = useNodeTransformActions(editor);
  return (
    <SubMenuTrigger
      icon={(props) => <Icon icon="repeat" {...props} />}
      label="Turn Into"
    >
      <DropdownMenuGroup>
        <DropdownMenuLabel>Turn into</DropdownMenuLabel>
        {actions.map((action) => (
          <BaseMenuItem key={action.label} {...action} />
        ))}
      </DropdownMenuGroup>
    </SubMenuTrigger>
  );
};

const ResetFormattingAction: React.FC = () => {
  const { canReset, handleResetFormatting, label, Icon } =
    useResetAllFormatting({
      hideWhenUnavailable: true,
      preserveMarks: ['inlineThread'],
    });

  return (
    <BaseMenuItem
      icon={Icon}
      label={label}
      disabled={!canReset}
      onClick={handleResetFormatting}
    />
  );
};

const ImageActionGroup: React.FC = () => {
  const { canDownload, handleDownload, label, Icon } = useImageDownload({
    hideWhenUnavailable: true,
  });

  return (
    <>
      <BaseMenuItem
        icon={Icon}
        label={label}
        disabled={!canDownload}
        onClick={handleDownload}
      />

      <DropdownMenuSeparator />
    </>
  );
};

const CoreActionGroup: React.FC = () => {
  const {
    handleDuplicate,
    canDuplicate,
    label,
    Icon: DuplicateIcon,
  } = useDuplicate();
  const {
    handleCopyToClipboard,
    canCopyToClipboard,
    label: copyLabel,
    Icon: CopyIcon,
  } = useCopyToClipboard();
  const {
    handleCopyAnchorLink,
    canCopyAnchorLink,
    label: copyAnchorLinkLabel,
    Icon: CopyAnchorLinkIcon,
  } = useCopyAnchorLink();

  return (
    <>
      <DropdownMenuGroup>
        <BaseMenuItem
          icon={DuplicateIcon}
          label={label}
          onClick={handleDuplicate}
          disabled={!canDuplicate}
          shortcutBadge={<DuplicateShortcutBadge />}
        />
        <BaseMenuItem
          icon={CopyIcon}
          label={copyLabel}
          onClick={handleCopyToClipboard}
          disabled={!canCopyToClipboard}
          shortcutBadge={<CopyToClipboardShortcutBadge />}
        />
        <BaseMenuItem
          icon={CopyAnchorLinkIcon}
          label={copyAnchorLinkLabel}
          onClick={handleCopyAnchorLink}
          disabled={!canCopyAnchorLink}
          shortcutBadge={<CopyAnchorLinkShortcutBadge />}
        />
      </DropdownMenuGroup>

      <DropdownMenuSeparator />
    </>
  );
};

const DeleteActionGroup: React.FC = () => {
  const { handleDeleteNode, canDeleteNode, label, Icon } = useDeleteNode();

  return (
    <DropdownMenuGroup>
      <BaseMenuItem
        icon={Icon}
        label={label}
        onClick={handleDeleteNode}
        disabled={!canDeleteNode}
        shortcutBadge={<DeleteNodeShortcutBadge />}
      />
    </DropdownMenuGroup>
  );
};

/**
 * Walk a table node to find positions inside its first and last cells. Used
 * to anchor row/column inserts from the drag-handle menu, which targets the
 * whole table rather than a specific cell.
 */
const getTableCellAnchors = (
  editor: Editor,
  tablePos: number
): { firstCellPos: number; lastCellPos: number } | null => {
  const tableNode = editor.state.doc.nodeAt(tablePos);
  if (!tableNode || tableNode.type.name !== 'table') {
    return null;
  }
  let firstCellPos: number | null = null;
  let lastCellPos: number | null = null;
  editor.state.doc.nodesBetween(
    tablePos + 1,
    tablePos + tableNode.nodeSize - 1,
    (n, pos) => {
      if (n.type.name === 'tableCell' || n.type.name === 'tableHeader') {
        // pos + 1 places the selection inside the cell's first child
        if (firstCellPos === null) {
          firstCellPos = pos + 1;
        }
        lastCellPos = pos + 1;
      }
      return true;
    }
  );
  if (firstCellPos === null || lastCellPos === null) {
    return null;
  }
  return { firstCellPos, lastCellPos };
};

const TableActionGroup: React.FC<{
  editor: Editor;
  tablePos: number;
}> = ({ editor, tablePos }) => {
  const anchors = getTableCellAnchors(editor, tablePos);
  if (!anchors) {
    return null;
  }

  const at = (cellPos: number) =>
    editor.chain().focus().setTextSelection(cellPos);
  const { firstCellPos, lastCellPos } = anchors;

  return (
    <SubMenuTrigger
      icon={(props) => <Icon icon="table" {...props} />}
      label="Table"
    >
      <DropdownMenuGroup>
        <DropdownMenuLabel>Rows</DropdownMenuLabel>
        <BaseMenuItem
          icon="plus"
          label="Insert row above"
          onClick={() => at(firstCellPos).addRowBefore().run()}
        />
        <BaseMenuItem
          icon="plus"
          label="Insert row below"
          onClick={() => at(lastCellPos).addRowAfter().run()}
        />
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuLabel>Columns</DropdownMenuLabel>
        <BaseMenuItem
          icon="plus"
          label="Insert column before"
          onClick={() => at(firstCellPos).addColumnBefore().run()}
        />
        <BaseMenuItem
          icon="plus"
          label="Insert column after"
          onClick={() => at(lastCellPos).addColumnAfter().run()}
        />
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuLabel>Headers</DropdownMenuLabel>
        <BaseMenuItem
          icon="view-columns"
          label="Toggle header row"
          onClick={() => at(firstCellPos).toggleHeaderRow().run()}
        />
        <BaseMenuItem
          icon="view-columns"
          label="Toggle header column"
          onClick={() => at(firstCellPos).toggleHeaderColumn().run()}
        />
      </DropdownMenuGroup>
    </SubMenuTrigger>
  );
};

export const DragContextMenu: React.FC<DragContextMenuProps> = ({
  editor: providedEditor,
  withSlashCommandTrigger = true,
  mobileBreakpoint = 768,
  ...props
}) => {
  const { editor } = useTiptapEditor(providedEditor);
  const { isDragging } = useUiEditorState(editor);
  const isMobile = useIsMobile(mobileBreakpoint);
  const [open, setOpen] = React.useState(false);
  const [node, setNode] = React.useState<TiptapNode | null>(null);
  const [nodePos, setNodePos] = React.useState<number>(-1);
  const [isDocumentTitle, setIsDocumentTitle] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const mouseDownPosRef = React.useRef<{ x: number; y: number } | null>(null);

  const handleNodeChange = React.useCallback((data: NodeChangeData) => {
    // Defer to avoid "flushSync was called from inside a lifecycle method" when
    // this is triggered during a parent re-render (e.g. from model scope updates).
    // setTimeout(0) pushes to next macrotask; queueMicrotask can still run during
    // React's commit phase and trigger the error.
    setTimeout(() => {
      flushSync(() => {
        if (data.node) {
          setNode(data.node);
          // Check if this is the document title (H1 at position 0)
          const isTitle =
            data.pos === 0 &&
            data.node.type.name === 'heading' &&
            data.node.attrs['level'] === 1;
          setIsDocumentTitle(isTitle);
        }
        setNodePos(data.pos);
      });
    }, 0);
  }, []);

  React.useEffect(() => {
    if (!editor) {
      return;
    }
    editor.commands.setLockDragHandle(open);
    editor.commands.setMeta('lockDragHandle', open);
  }, [editor, open]);

  const {
    hasAnyActionGroups,
    hasColorActions,
    hasTransformActions,
    hasResetFormatting,
    hasImage,
  } = useMenuActionVisibility(editor);

  const dynamicPositions = React.useMemo(() => {
    return {
      middleware: [
        offset((props) => {
          const { rects } = props;
          const nodeHeight = rects.reference.height;
          const dragHandleHeight = rects.floating.height;

          const crossAxis = nodeHeight / 2 - dragHandleHeight / 2;

          return {
            mainAxis: 6, // Reduced from 16 to 6 to bring it closer to content
            // if height is more than 40px, then it's likely a block node
            crossAxis: nodeHeight > 40 ? 0 : crossAxis,
          };
        }),
      ],
    };
  }, []);

  const handleOnMenuClose = React.useCallback(() => {
    if (editor) {
      editor.commands.setMeta('hideDragHandle', true);
    }
  }, [editor]);

  const onElementDragStart = React.useCallback(() => {
    if (!editor) {
      return;
    }
    editor.commands.setIsDragging(true);
  }, [editor]);

  const onElementDragEnd = React.useCallback(() => {
    if (!editor) {
      return;
    }
    editor.commands.setIsDragging(false);
  }, [editor]);

  // Memoize button rect to avoid multiple getBoundingClientRect() calls
  const rect = buttonRef.current?.getBoundingClientRect() ?? {
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  };
  const buttonRect = {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };

  if (!editor) {
    return null;
  }

  return (
    <DragHandle
      editor={editor}
      onNodeChange={handleNodeChange}
      computePositionConfig={dynamicPositions}
      onElementDragStart={onElementDragStart}
      onElementDragEnd={onElementDragEnd}
      {...props}
    >
      <div
        className="flex items-center gap-1"
        style={{
          ...(isMobile || isTextSelectionValid(editor) || isDocumentTitle
            ? { opacity: 0, pointerEvents: 'none' }
            : {}),
          ...(isDragging ? { opacity: 0 } : {}),
        }}
      >
        {withSlashCommandTrigger && (
          <SlashCommandTriggerButton
            node={node}
            nodePos={nodePos}
            data-weight="small"
          />
        )}
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <div className="relative">
            <Button
              ref={buttonRef}
              variant="ghost"
              size="icon-sm"
              tabIndex={-1}
              style={{
                cursor: 'grab',
              }}
              onMouseDown={(e) => {
                mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
                selectNodeAndHideFloating(editor, nodePos);
              }}
              onClick={(e) => {
                // Only open menu if it was a click (not a drag)
                // Check if mouse moved less than 5px from mousedown position
                if (mouseDownPosRef.current) {
                  const deltaX = Math.abs(
                    e.clientX - mouseDownPosRef.current.x
                  );
                  const deltaY = Math.abs(
                    e.clientY - mouseDownPosRef.current.y
                  );
                  if (deltaX < 5 && deltaY < 5) {
                    setOpen(!open);
                  }
                } else {
                  setOpen(!open);
                }
                mouseDownPosRef.current = null;
              }}
            >
              {!open && !isDragging ? (
                <Tooltip
                  tooltipContent="Click for options, Hold for drag"
                  side="auto"
                >
                  <Icon
                    icon="grip"
                    className="tiptap-button-icon text-muted-foreground"
                  />
                </Tooltip>
              ) : (
                <Icon
                  icon="grip"
                  className="tiptap-button-icon text-muted-foreground"
                />
              )}
            </Button>
            <DropdownMenuTrigger asChild>
              <button
                style={{
                  position: 'fixed',
                  left: `${buttonRect.left}px`,
                  top: `${buttonRect.top}px`,
                  width: `${buttonRect.width}px`,
                  height: `${buttonRect.height}px`,
                  pointerEvents: 'none',
                  opacity: 0,
                  border: 'none',
                  background: 'none',
                  padding: 0,
                }}
                tabIndex={-1}
              />
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent
            side="left"
            align="start"
            className="min-w-[15rem]"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              handleOnMenuClose();
            }}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                {getNodeDisplayName(editor)}
              </DropdownMenuLabel>
              {hasColorActions && <ColorActionGroup />}
              {hasTransformActions && <TransformActionGroup editor={editor} />}
              {hasResetFormatting && <ResetFormattingAction />}
              {hasImage && <ImageActionGroup />}
              {node?.type.name === 'table' && nodePos >= 0 && (
                <TableActionGroup editor={editor} tablePos={nodePos} />
              )}
            </DropdownMenuGroup>

            {(hasAnyActionGroups || node?.type.name === 'table') && (
              <DropdownMenuSeparator />
            )}

            <CoreActionGroup />

            <DeleteActionGroup />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </DragHandle>
  );
};
